import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { take, switchMap } from 'rxjs/operators';
import { Observable, from, of } from 'rxjs';
import { SessionInitService } from '../../services/session-init.service';
import { CustomerdatabaseService } from '../../services/customerdatabase.service';
import { AngularFireAuth } from '@angular/fire/auth';
import { MenuService } from '../../services/menu-service';
import { isHybridPlan, resolveMenuRoleForPlan } from '../helper/plan-routing.helper';

@Injectable({
  providedIn: 'root'
})
export class EnhancedAuthGuard implements CanActivate {
  private autoLoginAttempted = false;
  private isAgentsSubdomain = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private sessionInit: SessionInitService,
    private customerDB: CustomerdatabaseService,
    private afAuth: AngularFireAuth,
    private menuService: MenuService
  ) {
    this.isAgentsSubdomain = window.location.hostname === 'agents.monaai.de' || window.location.hostname === 'localhost';
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const url = state.url;
    console.log('[EnhancedAuthGuard] Checking auth for URL:', url);
    
    // Fast path for agents subdomain accessing dashboard
    if (this.isAgentsSubdomain && url.includes('agent-dashboard')) {
      console.log('[EnhancedAuthGuard] Fast path for agents.monaai.de dashboard access');
      
      return this.afAuth.authState.pipe(
        take(1),
        switchMap(async (user) => {
          if (user) {
            if (this.authService.isLoggedIn() && this.customerDB.isDBConnected()) {
              console.log('[EnhancedAuthGuard] Already authenticated and DB connected');
              this.ensureMenuInitialized(url);
              return true;
            }
            
            // const cachedPlan = localStorage.getItem('userPlan');
            // const cachedUid = localStorage.getItem('uid');
            // if (cachedPlan && cachedUid === user.uid) {
            //   console.log('[EnhancedAuthGuard] Optimistic access with cached plan:', cachedPlan);
            //   this.customerDB.setcurrentPlan(cachedPlan);
            //   this.ensureMenuInitialized(url);
            //   return true;
            // }
            
            console.log('[EnhancedAuthGuard] Performing minimal login for agents');
            const success = await this.sessionInit.minimalAgentsLogin(user);
            if (success) {
              const currentPlan = this.customerDB.returnCurrentPlan() || '';
              if (!currentPlan) {
                this.router.navigate(['/pricing']);
                return false;
              }
              this.ensureMenuInitialized(url);
              return true;
            }
          }
          
          sessionStorage.setItem('redirectUrl', url);
          this.router.navigate(['/login']);
          return false;
        })
      );
    }
    
    // Normal flow for other routes
    return this.authService.user$.pipe(
      take(1),
      switchMap(async (user) => {
        // User is already authenticated
        if (user || this.authService.isLoggedIn()) {
          console.log('[EnhancedAuthGuard] User already authenticated');
          
          const isDBConn = this.customerDB.isDBConnected();
          console.log('[EnhancedAuthGuard] isDBConnected:', isDBConn, 'app2:', this.customerDB.app2 !== null);

          // Send customers landing on the generic dashboard to their product home
          if (url === '/dashboard' || url === '/') {
            const currentPlan = this.customerDB.returnCurrentPlan() || '';
            if (isHybridPlan(currentPlan)) {
              console.log('[EnhancedAuthGuard] Redirecting hybrid customer to hybrid-dashboard');
              this.router.navigate(['/hybrid-dashboard']);
              return false;
            }
            if (this.customerDB.getIsAgentsCustomer() && currentPlan.includes('agents')) {
              console.log('[EnhancedAuthGuard] Redirecting agents customer to agent-dashboard');
              this.router.navigate(['/agent-dashboard']);
              return false;
            }
          }

          // Check if database connection is required for this route
          const requiresDB = this.routeRequiresDatabase(url);
          console.log('[EnhancedAuthGuard] Route requiresDB:', requiresDB, 'for URL:', url);
          if (requiresDB && !this.customerDB.isDBConnected()) {
            console.log('[EnhancedAuthGuard] Database not connected, attempting to connect...');
            
            // Try auto-login which includes database connection
            try {
              let autoLoginSuccess = false;
              
              if (this.isAgentsSubdomain && url.includes('agent-dashboard')) {
                // Use minimal login for agents subdomain accessing dashboard
                const currentUser = await this.afAuth.authState.pipe(take(1)).toPromise();
                if (currentUser) {
                  autoLoginSuccess = await this.sessionInit.minimalAgentsLogin(currentUser);
                }
              } else {
                // Use full login for other cases
                autoLoginSuccess = await this.sessionInit.autoLoginFromCookieAndInitialize();
              }
              
              if (autoLoginSuccess) {
                const currentPlan = this.customerDB.returnCurrentPlan() || '';
                if (!currentPlan && this.routeRequiresDatabase(url)) {
                  console.log('[EnhancedAuthGuard] No plan after auto-login, redirecting to pricing');
                  this.router.navigate(['/pricing']);
                  return false;
                }
                console.log('[EnhancedAuthGuard] Database connected after auto-login');
                // Ensure menu state is properly initialized
                this.ensureMenuInitialized(url);
                return true;
              } else {
                console.log('[EnhancedAuthGuard] Failed to connect database');
                sessionStorage.setItem('redirectUrl', url);
                this.router.navigate(['/login']);
                return false;
              }
            } catch (error) {
              console.error('[EnhancedAuthGuard] Error connecting database:', error);
              sessionStorage.setItem('redirectUrl', url);
              this.router.navigate(['/login']);
              return false;
            }
          }

          const currentPlan = this.customerDB.returnCurrentPlan() || '';
          if (requiresDB && this.customerDB.isDBConnected() && !currentPlan) {
            console.log('[EnhancedAuthGuard] No plan found after DB init, redirecting to pricing');
            this.router.navigate(['/pricing']);
            return false;
          }
          
          // Ensure menu state is properly initialized for already authenticated users
          this.ensureMenuInitialized(url);
          return true;
        }
        
        // Special case for teammember URLs
        if (url.includes('teammember')) {
          return true;
        }
        
        // Try auto-login if not attempted yet
        if (!this.autoLoginAttempted) {
          console.log('[EnhancedAuthGuard] Attempting auto-login for URL:', url);
          this.autoLoginAttempted = true;
          
          try {
            let autoLoginSuccess = false;
            
            if (this.isAgentsSubdomain && url.includes('agent-dashboard')) {
              // Use minimal login for agents subdomain accessing dashboard
              const currentUser = await this.authService.waitForAuthState();
              if (currentUser) {
                autoLoginSuccess = await this.sessionInit.minimalAgentsLogin(currentUser);
              }
            } else {
              // Use full login for other cases
              autoLoginSuccess = await this.sessionInit.autoLoginFromCookieAndInitialize();
            }
            
            if (autoLoginSuccess) {
              const currentPlan = this.customerDB.returnCurrentPlan() || '';
              if (!currentPlan && this.routeRequiresDatabase(url)) {
                console.log('[EnhancedAuthGuard] No plan after auto-login, redirecting to pricing');
                this.router.navigate(['/pricing']);
                return false;
              }
              console.log('[EnhancedAuthGuard] Auto-login successful');
              // Ensure menu state is properly initialized
              this.ensureMenuInitialized(url);
              return true;
            } else {
              console.log('[EnhancedAuthGuard] Auto-login failed');
              sessionStorage.setItem('redirectUrl', url);
              this.router.navigate(['/login']);
              return false;
            }
          } catch (error) {
            console.error('[EnhancedAuthGuard] Auto-login error:', error);
            sessionStorage.setItem('redirectUrl', url);
            this.router.navigate(['/login']);
            return false;
          }
        }
        
        // Not authenticated and auto-login already attempted
        sessionStorage.setItem('redirectUrl', url);
        this.router.navigate(['/login']);
        return false;
      })
    );
  }

  private ensureMenuInitialized(currentUrl?: string) {
    try {
      const plan = this.customerDB.returnCurrentPlan() || '';
      if (plan) {
        this.menuService.setMenuForRole(resolveMenuRoleForPlan(plan, {
          kundennummer: this.customerDB.getCustomerNumber(),
          canAccessHybrid: this.customerDB.canAccessHybridFeatures()
        }));
      }
    } catch {}
    // If still not initialized, pick sensible defaults based on URL or subdomain
    if (!this.menuService.isMenuInitialized()) {
      const isAgentsContext = this.isAgentsSubdomain ||
        (currentUrl && (
          currentUrl.includes('/agent-dashboard') ||
          currentUrl.includes('/mona-agent-chat') ||
          currentUrl.includes('/mona-agent-builder') ||
          currentUrl.includes('/data-pipeline') ||
          currentUrl.includes('/email-inbox') ||
          currentUrl.includes('/vms-form-automation')
        ));
      const hasPlan = !!(this.customerDB.returnCurrentPlan() || '');
      if (isAgentsContext && hasPlan) {
        this.menuService.setMenuForRole('agents-free');
      }
    }
    if (!this.menuService.isMenuInitialized()) {
      this.menuService.initializeMenuState();
    } else {
      this.menuService.initializeMenuState();
    }
  }

  private routeRequiresDatabase(url: string): boolean {
    const noDbRoutes = [
      '/login',
      '/register',
      '/signup',
      '/pricing',              // New users need to select a plan before having a kundennummer
      '/account-confirmation', // Shown after signup before email verification
      '/waitlist-confirm',     // Candidate-facing waitlist acceptance page (token-based auth)
      '/emailverify',          // Email verification page
      '/success'               // Post-checkout redirect from Stripe, handles its own routing
    ];
    
    if (noDbRoutes.some(route => url.includes(route))) {
      return false;
    }
    return true;
  }
}
